import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Youtube, Download, Loader2, Clock } from 'lucide-react';
import { honorableMusicSongs, honorableMusicYouTube } from '../data/honorableMusic';
interface SongListProps {
  /** Called when a track starts playing (e.g. to pause background audio) */
  onTrackPlay?: () => void;
}
export function SongList({
  onTrackPlay
}: SongListProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState<string | null>(null);
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio?.pause();
    };
  }, []);
  const toggleTrack = (url: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (activeUrl === url) {
      if (audio.paused) {
        onTrackPlay?.();
        audio.play().catch(() => {});
      } else {
        audio.pause();
      }
      return;
    }
    audio.src = url;
    setActiveUrl(url);
    setProgress(0);
    onTrackPlay?.();
    audio.play().catch(() => {});
  };
  const downloadTrack = async (url: string, title: string) => {
    setDownloading(url);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${title} - Honorable Music.mp3`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback: open the file directly so the browser can save it
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(null);
    }
  };
  return <div data-id="element-60" data-mp="1fjz1tq91mk39">
      <audio ref={audioRef} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onEnded={() => {
      setIsPlaying(false);
      setProgress(0);
    }} onTimeUpdate={e => {
      const a = e.currentTarget;
      if (a.duration) setProgress(a.currentTime / a.duration);
    }} data-id="element-61" data-mp="1fjfluf2ye5rv" />

      <ol className="divide-y divide-green-500/15 rounded-2xl border border-green-500/30 bg-white/[0.03] overflow-hidden" data-id="element-62" data-mp="1c21czo4k0gfj">
        {honorableMusicSongs.map((song, index) => {
        const number = String(index + 1).padStart(2, '0');
        if (!song.url) {
          return <li key={song.title} className="flex items-center gap-4 px-5 md:px-6 py-4" data-id="element-63" data-mp="ocqgiiu8l877">
                <span className="w-8 text-sm font-semibold text-slate-600 tabular-nums" data-id="element-64" data-mp="tcuykdasmazj">
                  {number}
                </span>
                <span className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center bg-white/5 text-slate-500" aria-hidden="true" data-id="element-65" data-mp="fe51gxnz4bb6">
                  <Clock className="w-5 h-5" data-id="element-66" data-mp="r6f3h3z473b" />
                </span>
                <span className="flex-1 min-w-0 text-lg md:text-xl font-bold text-slate-400 truncate" data-id="element-67" data-mp="3rx7hz2dsbudv">
                  {song.title}
                </span>
                <span className="flex-shrink-0 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 text-xs font-semibold whitespace-nowrap" data-id="element-68" data-mp="4qf8ulz0k4yv">
                  Coming Soon
                </span>
              </li>;
        }
        const url = song.url;
        const isActive = activeUrl === url;
        const playingThis = isActive && isPlaying;
        const isDownloading = downloading === url;
        return <li key={song.title} className="flex items-center" data-id="element-69" data-mp="oh0o1nl69bia" data-mpi={index}>
              <button type="button" onClick={() => toggleTrack(url)} className="group flex-1 min-w-0 flex items-center gap-4 pl-5 md:pl-6 pr-2 py-4 hover:bg-green-500/5 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-400" aria-label={`${playingThis ? 'Pause' : 'Play'} ${song.title}`} aria-pressed={playingThis} data-id="element-70" data-mp="12fgy9oi8tk56">
                <span className="w-8 text-sm font-semibold text-slate-500 tabular-nums text-left" data-id="element-71" data-mp="1v9n48rojiyuk">
                  {number}
                </span>
                <span className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-150 ${playingThis ? 'bg-green-400 text-black' : 'bg-green-500/15 text-green-400 group-hover:bg-green-500 group-hover:text-black'}`} aria-hidden="true" data-id="element-72" data-mp="yknut9c7zr0s">
                  {playingThis ? <Pause className="w-5 h-5" fill="currentColor" data-id="element-73" data-mp="2nwzrz56ftcpg" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" data-id="element-74" data-mp="cayxhtjwy69v" />}
                </span>
                <span className="flex-1 min-w-0 text-left" data-id="element-75" data-mp="7cdy1wchjqmh">
                  <span className="block text-lg md:text-xl font-bold text-white truncate" data-id="element-76" data-mp="2kmhmen1n1a2">
                    {song.title}
                  </span>
                  <span className="block text-sm text-slate-400" data-id="element-77" data-mp="3tq5sxgl50yj1">
                    {playingThis ? 'Now playing' : isActive ? 'Paused' : 'Tap to play'}
                  </span>
                  {isActive && <span className="mt-2 block h-1 rounded-full bg-white/10 overflow-hidden" data-id="element-78" data-mp="3fro7jpipfy4a">
                      <span className="block h-full bg-green-400" style={{
                  width: `${progress * 100}%`
                }} data-id="element-79" data-mp="3atcuz710z9id" />
                    </span>}
                </span>
              </button>

              <button type="button" onClick={() => downloadTrack(url, song.title)} disabled={isDownloading} className="flex-shrink-0 mr-4 md:mr-5 w-10 h-10 rounded-full flex items-center justify-center border border-slate-600 text-slate-300 hover:border-green-400 hover:text-green-400 disabled:opacity-60 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-400" aria-label={`Download ${song.title}`} title={`Download ${song.title}`} data-id="element-80" data-mp="33ger7jvpo695">
                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-81" data-mp="3d66ncijgl7fq" /> : <Download className="w-4 h-4" aria-hidden="true" data-id="element-82" data-mp="3h6qodx75kv75" />}
              </button>
            </li>;
      })}
      </ol>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-4" data-id="element-83" data-mp="25to68xpenxkv">
        <p className="text-slate-400 text-sm" data-id="element-84" data-mp="1y9n8oepmt3fk">
          Catch the music videos on YouTube
        </p>
        <a href={honorableMusicYouTube} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF0000] hover:bg-[#E00000] text-white font-semibold whitespace-nowrap transition-colors duration-150" data-id="element-85" data-mp="1takdrn3d8dlb">
          <Youtube className="w-5 h-5" aria-hidden="true" data-id="element-86" data-mp="1fiw3lpcofz6z" />
          Subscribe on YouTube
        </a>
      </div>
    </div>;
}